import { createContext, useContext } from 'react';
import { identityApi } from '../api/client';
import {
  BRANDING_STATUS,
  brandingStateFromResponse,
  fallbackBrandingState,
  isMainPortalHostname,
} from './brandingPresentation';

// ─── Portal Branding State ─────────────────────────────────────────────────────
// Context, hook and portal-scoped loader for the shared branding state. Kept
// separate from the provider component so this module exports no components
// (and the provider exports nothing else), which keeps Vite fast refresh happy.
//
// Branding is presentation only and never participates in authentication or
// tenant authorization: the backend's trusted ingress contract remains the sole
// authority. Nothing here touches localStorage, sessionStorage or the session
// store.

// Resolved branding is memoised per browser origin, so navigating inside one
// portal does not refetch while a different origin — a different document
// entirely — can never read another portal's entry. Only responses that pass
// full contract validation are stored: neither a failure nor a malformed success
// is cached, so a transient error cannot permanently pin the portal to the
// fallback and a bad payload cannot become permanently cached branding.
const successCache = new Map();
const inFlight = new Map();
const invalidationListeners = new Set();

// Without a provider every consumer sees the fixed AlgoSys visual defaults with
// AlgoSys-specific marketing suppressed, which is the conservative fallback for
// a component rendered outside the branded tree.
export const BrandingContext = createContext(fallbackBrandingState(BRANDING_STATUS.UNRESOLVED));

export function useBranding() {
  return useContext(BrandingContext);
}

export function currentPortalKey() {
  if (typeof window === 'undefined' || !window.location) return 'unknown-origin';
  return window.location.origin || `${window.location.protocol}//${window.location.host}`;
}

// Presentation-only: used solely to decide whether AlgoSys's own marketing
// content may be shown. See `isMainPortalHostname`.
export function currentPortalIsMain() {
  if (typeof window === 'undefined' || !window.location) return false;
  return isMainPortalHostname(window.location.hostname);
}

export function getCachedPortalBranding(portalKey) {
  return successCache.get(portalKey);
}

/**
 * Resolve branding for one portal. Concurrent callers share a single in-flight
 * request, and a cached success resolves without touching the network.
 *
 * Rejects when the response does not satisfy the public contract, so the caller
 * applies its neutral fallback and a later attempt can still succeed.
 */
export function loadPortalBranding(portalKey, { isMainPortal = false } = {}) {
  const cached = successCache.get(portalKey);
  if (cached) return Promise.resolve(cached);

  const pending = inFlight.get(portalKey);
  if (pending) return pending;

  const request = identityApi
    .portalBranding()
    .then((data) => {
      const resolved = brandingStateFromResponse(data, { isMainPortal });
      if (!resolved) {
        throw new Error('Portal branding response did not match the public contract');
      }
      successCache.set(portalKey, resolved);
      return resolved;
    })
    .finally(() => {
      inFlight.delete(portalKey);
    });

  inFlight.set(portalKey, request);
  return request;
}

// Exported for tests only: clears the module-level portal memo between cases.
export function __resetPortalBrandingCache() {
  successCache.clear();
  inFlight.clear();
  invalidationListeners.clear();
}

/**
 * Subscribe to branding invalidations raised by `refreshCurrentPortalBranding`.
 * The listener receives the invalidated portal key; a mounted BrandingProvider
 * compares it with its own origin before re-resolving, so one portal's refresh
 * can never disturb another origin's branding.
 */
export function subscribeToPortalBrandingInvalidation(listener) {
  invalidationListeners.add(listener);
  return () => invalidationListeners.delete(listener);
}

/**
 * Drop the current portal's cached branding so mounted providers re-resolve it
 * (and its custom logo) without a browser refresh. Used after the Operator
 * changes their own logo from the authenticated settings page.
 *
 * Scoped to the current origin and only a positively resolved branded portal:
 * the main AlgoSys portal keeps its cached fixed defaults untouched (nothing
 * changes there), other origins are never touched, and no Operator identifier
 * crosses origins. Returns whether an invalidation actually happened.
 */
export function refreshCurrentPortalBranding() {
  const key = currentPortalKey();
  const cached = successCache.get(key);
  if (!cached || cached.status !== BRANDING_STATUS.BRANDED) return false;
  successCache.delete(key);
  for (const listener of invalidationListeners) {
    try {
      listener(key);
    } catch {
      // A listener must never break the Operator flow that caused the refresh.
    }
  }
  return true;
}
